import { Navigate, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { EmptyState, LoadingState } from '@cca/ui';
import { useSession } from '../lib/session.js';

/**
 * Client-side route guard.
 *
 * This is a convenience, not a security control: it decides what to *render*,
 * never what a user may access. Every protected route is enforced server-side
 * by `requireAuth` + `requirePermission` in the worker, and by RLS beneath that.
 * Removing this component would change the UI and nothing else.
 */
export function RequireSession({ children }: { readonly children: ReactNode }) {
  const { session, loading, configured } = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (loading) return <LoadingState label="Checking session" />;

  if (!configured) {
    return (
      <EmptyState
        title="Authentication is not configured"
        description="This environment has no Supabase connection. Set PUBLIC_SUPABASE_URL and PUBLIC_SUPABASE_ANON_KEY, then restart the worker."
      />
    );
  }

  if (!session) {
    return <Navigate to="/sign-in" search={{ redirect: pathname }} replace />;
  }

  return <>{children}</>;
}
